import { lazy } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'

const DashboardPage = lazy(() =>
  import('../pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
)
const AssessmentsPage = lazy(() =>
  import('../pages/AssessmentsPage').then((m) => ({ default: m.AssessmentsPage }))
)
const RisksPage = lazy(() =>
  import('../pages/RisksPage').then((m) => ({ default: m.RisksPage }))
)
const CompliancePage = lazy(() =>
  import('../pages/CompliancePage').then((m) => ({ default: m.CompliancePage }))
)
const GovernancePage = lazy(() =>
  import('../pages/GovernancePage').then((m) => ({ default: m.GovernancePage }))
)
const AIChatPage = lazy(() =>
  import('../pages/AIChatPage').then((m) => ({ default: m.AIChatPage }))
)
const NLQueryPage = lazy(() =>
  import('../pages/NLQueryPage').then((m) => ({ default: m.NLQueryPage ?? m.default }))
)
const AIPersonasPage = lazy(() =>
  import('../pages/AIPersonasPage').then((m) => ({ default: m.AIPersonasPage ?? m.default }))
)
const HypersyncPage = lazy(() =>
  import('../pages/HypersyncPage').then((m) => ({ default: m.HypersyncPage ?? m.default }))
)
const TrustCenterPage = lazy(() =>
  import('../pages/TrustCenterPage').then((m) => ({ default: m.TrustCenterPage ?? m.default }))
)
const SettingsPage = lazy(() =>
  import('../pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
)
const SettingsProfilePage = lazy(() =>
  import('../pages/settings/ProfilePage').then((m) => ({ default: m.ProfilePage }))
)
const SettingsNotificationsPage = lazy(() =>
  import('../pages/settings/NotificationsPage').then((m) => ({ default: m.NotificationsPage }))
)
// MULTI-JURISDICTION P6 — Préférences (langue/région/apparence + JurisdictionPicker).
const SettingsPreferencesPage = lazy(() =>
  import('../pages/settings/PreferencesPage').then((m) => ({ default: m.PreferencesPage }))
)
const SettingsSectionPage = lazy(() =>
  import('../pages/settings/SettingsSectionPage').then((m) => ({ default: m.SettingsSectionPage }))
)
const NotFoundPage = lazy(() =>
  import('../pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
)

// Assessment module pages
const AssessmentCenterPage = lazy(() =>
  import('../pages/assessments').then((m) => ({ default: m.AssessmentCenterPage }))
)
const CreateAssessmentPage = lazy(() =>
  import('../pages/assessments').then((m) => ({ default: m.CreateAssessmentPage }))
)
const QuestionnairePage = lazy(() =>
  import('../pages/assessments').then((m) => ({ default: m.QuestionnairePage }))
)
const ScoringDashboardPage = lazy(() =>
  import('../pages/assessments').then((m) => ({ default: m.ScoringDashboardPage }))
)
const GapAnalysisPage = lazy(() =>
  import('../pages/assessments').then((m) => ({ default: m.GapAnalysisPage }))
)

// Frameworks pages
const FrameworksPage = lazy(() =>
  import('../pages/frameworks').then((m) => ({ default: m.FrameworksPage }))
)
const FrameworkDetailPage = lazy(() =>
  import('../pages/frameworks').then((m) => ({ default: m.FrameworkDetailPage }))
)
const FrameworkControlsPage = lazy(() =>
  import('../pages/frameworks').then((m) => ({ default: m.FrameworkControlsPage }))
)

// Controls pages
const ControlLibraryPage = lazy(() =>
  import('../pages/controls').then((m) => ({ default: m.ControlLibraryPage }))
)
const ControlDetailPage = lazy(() =>
  import('../pages/controls').then((m) => ({ default: m.ControlDetailPage }))
)
const ControlStatusPage = lazy(() =>
  import('../pages/controls').then((m) => ({ default: m.ControlStatusPage }))
)

// Evidence pages
const EvidenceRepositoryPage = lazy(() =>
  import('../pages/evidence').then((m) => ({ default: m.EvidenceRepositoryPage }))
)
const EvidenceDetailPage = lazy(() =>
  import('../pages/evidence').then((m) => ({ default: m.EvidenceDetailPage }))
)

// Reports pages
const ReportsPage = lazy(() => import('../pages/reports/ReportsPage'))
const AdsManagerPage = lazy(() => import('../pages/ads/AdsManagerPage'))

// Integrations pages
const IntegrationsPage = lazy(() => import('../pages/integrations/IntegrationsPage'))

// Compliance detail pages
const PIAPage = lazy(() => import('../pages/compliance/PIAPage'))
const DSRPage = lazy(() => import('../pages/compliance/DSRPage'))

// Audit pages
const AuditFindingsPage = lazy(() => import('../pages/audit/AuditFindingsPage'))

// Risk detail pages
const RiskHeatmapPage = lazy(() =>
  import('../pages/risks/RiskHeatmapPage').then((m) => ({ default: m.RiskHeatmapPage }))
)
const RiskAssessmentWizardPage = lazy(() =>
  import('../pages/risks/RiskAssessmentWizardPage').then((m) => ({ default: m.RiskAssessmentWizardPage }))
)
const RiskDetailPage = lazy(() =>
  import('../pages/risks/RiskDetailPage').then((m) => ({ default: m.RiskDetailPage }))
)

// Admin pages
const UserManagementPage = lazy(() =>
  import('../pages/admin/UserManagementPage').then((m) => ({ default: m.UserManagementPage }))
)
const RoleManagementPage = lazy(() =>
  import('../pages/admin/RoleManagementPage').then((m) => ({ default: m.RoleManagementPage }))
)
const OrganizationPage = lazy(() =>
  import('../pages/admin/OrganizationPage').then((m) => ({ default: m.OrganizationPage }))
)
const SSOConfigPage = lazy(() =>
  import('../pages/admin/SSOConfigPage').then((m) => ({ default: m.SSOConfigPage }))
)
const ScimProvisioningPage = lazy(() =>
  import('../pages/admin/ScimProvisioningPage').then((m) => ({ default: m.ScimProvisioningPage }))
)
const AuditTrailPage = lazy(() =>
  import('../pages/admin/AuditTrailPage').then((m) => ({ default: m.AuditTrailPage }))
)
const TrustCenterAdminPage = lazy(() =>
  import('../pages/admin/TrustCenterPage').then((m) => ({ default: m.TrustCenterPage }))
)
// Persisted Agents #22 (ai-orchestrator /api/v2/agents/*)
const AgentsPage = lazy(() =>
  import('../pages/admin/AgentsPage').then((m) => ({ default: m.AgentsPage }))
)
// CONTROL-PLANE (#26) — platform-admin scoped tenant lifecycle + FinOps + drift
const ControlPlanePage = lazy(() =>
  import('../pages/admin/ControlPlanePage').then((m) => ({ default: m.ControlPlanePage }))
)
// Certifications #29 — platform certifications register + data-residency (settings /api/v2/platform/*)
const CertificationsPage = lazy(() =>
  import('../pages/admin/CertificationsPage').then((m) => ({ default: m.CertificationsPage }))
)
// Board Room #27 — board meetings / packs / motions+voting / minutes+signing (governance /api/v1/board-room/*)
const BoardRoomPage = lazy(() =>
  import('../pages/admin/BoardRoomPage').then((m) => ({ default: m.BoardRoomPage }))
)

// Consents (V3.0)
const ConsentsPendingPage = lazy(() => import('../pages/consents/ConsentsPendingPage'))
const ConsentsActivePage = lazy(() => import('../pages/consents/ConsentsActivePage'))
const ConsentsHistoryPage = lazy(() => import('../pages/consents/ConsentsHistoryPage'))

// New: Prestataires, Offres, Dossiers, Vault, Self-manage
const PrestatairesPage = lazy(() => import('../pages/prestataires/PrestatairesPage'))
const OffresRecuesPage = lazy(() => import('../pages/prestataires/OffresRecuesPage'))
const ContratsPage = lazy(() => import('../pages/prestataires/ContratsPage'))
const MesDossiersPage = lazy(() => import('../pages/mes-dossiers/MesDossiersPage'))
// COLLAB-09: Avancement de l'engagement (read-only client view)
const EngagementProgressPage = lazy(() => import('../pages/mes-dossiers/EngagementProgressPage'))
// COLLAB-11: Hub de l'engagement (vue consolidee + chronologie — read-only)
const EngagementHubPage = lazy(() => import('../pages/mes-dossiers/EngagementHubPage'))
const VaultPage = lazy(() => import('../pages/vault/VaultPage'))
const FinancesPage = lazy(() => import('../pages/self-manage/FinancesPage'))
const CreditsPage = lazy(() => import('../pages/account/CreditsPage').then((m) => ({ default: m.CreditsPage })))
const HubPage = lazy(() => import('../pages/HubPage').then((m) => ({ default: m.HubPage })))

// COLLAB-01: Provider<->Client messaging
const MessagesPage = lazy(() =>
  import('../pages/messages/MessagesPage').then((m) => ({ default: m.MessagesPage }))
)

export const coreRoutes: RouteObject[] = [
  { path: '/dashboard', element: <DashboardPage /> },
  { path: '/hub', element: <HubPage /> },

  // Assessments Module
  { path: '/assessments', element: <AssessmentCenterPage /> },
  { path: '/assessments/create', element: <CreateAssessmentPage /> },
  // Profile-MD alias (existing /create handles new flow)
  { path: '/assessments/new', element: <CreateAssessmentPage /> },
  { path: '/assessments/:id/questionnaire', element: <QuestionnairePage /> },
  { path: '/assessments/:id/scoring', element: <ScoringDashboardPage /> },
  { path: '/assessments/:id/gaps', element: <GapAnalysisPage /> },
  { path: '/assessments/:id', element: <ScoringDashboardPage /> },

  // Risk Management
  { path: '/risks', element: <RisksPage /> },
  { path: '/risks/new', element: <RisksPage /> },
  { path: '/risks/heatmap', element: <RiskHeatmapPage /> },
  { path: '/risks/assessment', element: <RiskAssessmentWizardPage /> },
  { path: '/risks/:id', element: <RiskDetailPage /> },

  // Compliance
  { path: '/compliance', element: <CompliancePage /> },
  { path: '/compliance/pia', element: <PIAPage /> },
  { path: '/compliance/dsr', element: <DSRPage /> },

  // Frameworks
  { path: '/frameworks', element: <FrameworksPage /> },
  // Activation flow lives on the library page (dialog). Register /new so the
  // legacy deep-link resolves to the library instead of the SPA catch-all.
  { path: '/frameworks/new', element: <FrameworksPage /> },
  { path: '/frameworks/:id', element: <FrameworkDetailPage /> },
  { path: '/frameworks/:id/controls', element: <FrameworkControlsPage /> },
  // Profile-MD alias (PME owner: sélectionner référentiels)
  { path: '/frameworks/select', element: <FrameworksPage /> },

  // Controls
  { path: '/controls', element: <ControlLibraryPage /> },
  { path: '/controls/new', element: <ControlLibraryPage /> },
  { path: '/controls/status', element: <ControlStatusPage /> },
  { path: '/controls/:id', element: <ControlDetailPage /> },

  // Evidence
  { path: '/evidence', element: <EvidenceRepositoryPage /> },
  { path: '/evidence/:id', element: <EvidenceDetailPage /> },
  // Profile-MD alias (PME owner: ajouter preuves)
  { path: '/evidence/new', element: <EvidenceRepositoryPage /> },
  // Projects aliases (PME owner workflow)
  { path: '/projects', element: <DashboardPage /> },
  { path: '/projects/list', element: <DashboardPage /> },
  { path: '/projects/new', element: <DashboardPage /> },
  // Privacy aliases (DPO workflow — Loi 25 / RGPD)
  { path: '/privacy/dsr/new', element: <DSRPage /> },
  { path: '/privacy/registers/treatments', element: <CompliancePage /> },
  { path: '/privacy/training/program', element: <CompliancePage /> },
  { path: '/privacy/transfers-international', element: <CompliancePage /> },

  // Governance
  { path: '/governance', element: <GovernancePage /> },
  { path: '/governance/policies/new', element: <GovernancePage /> },

  // AI Chat
  { path: '/chat', element: <AIChatPage /> },

  // Reports
  { path: '/reports', element: <ReportsPage /> },

  // Ads / Régie publicitaire
  { path: '/ads', element: <AdsManagerPage /> },

  // Integrations
  { path: '/integrations', element: <IntegrationsPage /> },

  // Legacy audit redirect
  { path: '/audit/findings', element: <Navigate to="/audit-pro/findings" replace /> },

  // Administration
  { path: '/admin/users', element: <UserManagementPage /> },
  { path: '/admin/roles', element: <RoleManagementPage /> },
  { path: '/admin/organization', element: <OrganizationPage /> },
  { path: '/admin/sso', element: <SSOConfigPage /> },
  { path: '/admin/scim-provisioning', element: <ScimProvisioningPage /> },
  { path: '/admin/audit-trail', element: <AuditTrailPage /> },
  { path: '/admin/trust-center', element: <TrustCenterAdminPage /> },
  // Persisted Agents #22 — governed agent registry (HITL gate on opposable actions)
  { path: '/admin/agents', element: <AgentsPage /> },
  // Control-Plane #26 — platform-admin tenant lifecycle + FinOps + config drift
  { path: '/admin/control-plane', element: <ControlPlanePage /> },
  // Certifications #29 — platform certifications register + data-residency config
  { path: '/admin/certifications', element: <CertificationsPage /> },
  // Board Room #27 — board meetings, assembled packs, motions+voting, minutes+signing
  { path: '/admin/board-room', element: <BoardRoomPage /> },

  // Consentements (V3.0)
  { path: '/consents/pending', element: <ConsentsPendingPage /> },
  { path: '/consents/active', element: <ConsentsActivePage /> },
  { path: '/consents/history', element: <ConsentsHistoryPage /> },
  { path: '/consents', element: <ConsentsPendingPage /> },

  // Mes Prestataires
  { path: '/prestataires', element: <PrestatairesPage /> },
  { path: '/prestataires/offres', element: <OffresRecuesPage /> },
  { path: '/prestataires/contrats', element: <ContratsPage /> },

  // Mes Dossiers
  { path: '/mes-dossiers', element: <MesDossiersPage /> },
  // COLLAB-09: Avancement de l'engagement (consolidé + rapports de statut)
  { path: '/mes-dossiers/avancement', element: <EngagementProgressPage /> },
  { path: '/engagements/avancement', element: <EngagementProgressPage /> },
  // COLLAB-11: Hub de l'engagement (vue consolidée + chronologie)
  { path: '/mes-dossiers/hub', element: <EngagementHubPage /> },
  { path: '/engagements/hub', element: <EngagementHubPage /> },

  // COLLAB-01: Messagerie (provider<->client per-engagement threads)
  { path: '/messages', element: <MessagesPage /> },
  { path: '/messages/:threadId', element: <MessagesPage /> },

  // Documents & Coffre-fort
  { path: '/vault', element: <VaultPage /> },

  // Auto-Gestion
  { path: '/self-manage/finances', element: <FinancesPage /> },
  { path: '/account/credits', element: <CreditsPage /> },
  { path: '/account/subscription', element: <FinancesPage /> },

  // Settings
  { path: '/settings/profile', element: <SettingsProfilePage /> },
  // Dedicated functional notifications page (real /settings/user/notifications
  // API). Declared before the dynamic /settings/:section so it wins in v6.
  { path: '/settings/notifications', element: <SettingsNotificationsPage /> },
  // Dedicated Appearance surface (theme mode + 10 accent swatches) — parity with
  // the cabinet portal's /settings/appearance. Declared before /settings/:section
  // so it doesn't fall into SettingsSectionPage's NotFound for unknown slugs.
  { path: '/settings/appearance', element: <SettingsPage initialTab="appearance" /> },
  // Préférences utilisateur (langue/région/format + juridictions multi-zone via
  // JurisdictionPicker). Static route declared before /settings/:section so it
  // wins over the config-driven section renderer (which has no "preferences" slug).
  { path: '/settings/preferences', element: <SettingsPreferencesPage /> },
  // Config-driven tenant-settings sections (finance / tax / organization /
  // governance / legal / billing). A :section param + matching wildcard so
  // every MD deep-link (e.g. /settings/finance/fiscal-year-end,
  // /settings/organization/legal-form=corporation) resolves to its section page.
  // SettingsSectionPage validates :section against the config and renders
  // NotFoundPage for unknown slugs. Static routes (/settings/profile) outrank
  // these dynamic ones in react-router v6, so they keep priority.
  { path: '/settings/:section', element: <SettingsSectionPage /> },
  { path: '/settings/:section/*', element: <SettingsSectionPage /> },
  { path: '/settings/*', element: <SettingsPage /> },

  // Profile-MD top-level /report/* aliases (auditor engagement workflow shortcuts)
  { path: '/report', element: <Navigate to="/audit-pro/report" replace /> },
  { path: '/report/draft', element: <Navigate to="/audit-pro/report/draft" replace /> },
  { path: '/report/publish', element: <Navigate to="/audit-pro/report/publish" replace /> },
  { path: '/report/quality-review', element: <Navigate to="/audit-pro/report/quality-review" replace /> },

  // Previously unregistered pages (pages exist but had no route entry)
  { path: '/nlquery', element: <NLQueryPage /> },
  { path: '/nlquery/*', element: <NLQueryPage /> },
  { path: '/ai-personas', element: <AIPersonasPage /> },
  { path: '/ai-personas/*', element: <AIPersonasPage /> },
  { path: '/hypersync', element: <HypersyncPage /> },
  { path: '/hypersync/*', element: <HypersyncPage /> },
  { path: '/trust-center', element: <TrustCenterPage /> },
  { path: '/trust-center/*', element: <TrustCenterPage /> },

  // 404
  { path: '*', element: <NotFoundPage /> },
]
